import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { maquilaService } from "~/services/maquila/maquilaService";
import type {
  MaquilaConsumptionFilters,
  MaquilaConsumptionInput,
  MaquilaInventoryFilters,
  MaquilaItemFormData,
  MaquilaMovementFilters,
  MaquilaMovementFormData,
  MaquilaRecipeFormData,
} from "~/types/maquila";

export const MAQUILA_AREAS_KEY = ["maquila-areas"];
export const MAQUILA_ITEMS_KEY = ["maquila-items"];
export const MAQUILA_INVENTORY_KEY = ["maquila-inventory"];
export const MAQUILA_MOVEMENTS_KEY = ["maquila-movements"];
export const MAQUILA_RECIPES_KEY = ["maquila-recipes"];
export const MAQUILA_CONSUMPTIONS_KEY = ["maquila-consumptions"];

// ============================================
// AREAS HOOKS
// ============================================

export function useMaquilaAreas() {
  return useQuery({
    queryKey: MAQUILA_AREAS_KEY,
    queryFn: () => maquilaService.getAreas(),
  });
}

export const MAQUILA_CLIENTS_KEY = ["maquila-clients"];

export function useMaquilaClientList() {
  return useQuery({
    queryKey: MAQUILA_CLIENTS_KEY,
    queryFn: () => maquilaService.getMaquilaClients(),
  });
}

// ============================================
// ITEMS HOOKS
// ============================================

export function useMaquilaItems(clientId?: string) {
  return useQuery({
    queryKey: [...MAQUILA_ITEMS_KEY, clientId],
    queryFn: () => maquilaService.getItems(clientId),
  });
}

export function useMaquilaItem(id: string) {
  return useQuery({
    queryKey: [...MAQUILA_ITEMS_KEY, id],
    queryFn: () => maquilaService.getItemById(id),
    enabled: !!id,
  });
}

export function useCreateMaquilaItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: MaquilaItemFormData) => maquilaService.createItem(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MAQUILA_ITEMS_KEY });
      toast.success("Material creado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al crear material: ${error.message}`);
    },
  });
}

export function useUpdateMaquilaItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<MaquilaItemFormData>;
    }) => maquilaService.updateItem(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MAQUILA_ITEMS_KEY });
      toast.success("Material actualizado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al actualizar material: ${error.message}`);
    },
  });
}

export function useDeleteMaquilaItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => maquilaService.deleteItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MAQUILA_ITEMS_KEY });
      queryClient.invalidateQueries({ queryKey: MAQUILA_INVENTORY_KEY });
      toast.success("Material eliminado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al eliminar material: ${error.message}`);
    },
  });
}

// ============================================
// INVENTORY HOOKS
// ============================================

export function useMaquilaInventory(filters?: MaquilaInventoryFilters) {
  return useQuery({
    queryKey: [...MAQUILA_INVENTORY_KEY, filters],
    queryFn: () => maquilaService.getInventory(filters),
  });
}

export function useMaquilaInventoryByArea() {
  return useQuery({
    queryKey: [...MAQUILA_INVENTORY_KEY, "by-area"],
    queryFn: () => maquilaService.getInventoryByArea(),
  });
}

export function useMaquilaItemInventory(itemId: string) {
  return useQuery({
    queryKey: [...MAQUILA_INVENTORY_KEY, "item", itemId],
    queryFn: () => maquilaService.getItemInventory(itemId),
    enabled: !!itemId,
  });
}

// ============================================
// MOVEMENTS HOOKS
// ============================================

export function useMaquilaMovements(filters?: MaquilaMovementFilters) {
  return useQuery({
    queryKey: [...MAQUILA_MOVEMENTS_KEY, filters],
    queryFn: () => maquilaService.getMovements(filters),
  });
}

export function useCreateMaquilaMovement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: MaquilaMovementFormData) =>
      maquilaService.createMovement(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MAQUILA_INVENTORY_KEY });
      queryClient.invalidateQueries({ queryKey: MAQUILA_MOVEMENTS_KEY });
      toast.success("Movimiento registrado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al registrar movimiento: ${error.message}`);
    },
  });
}

// ============================================
// PRODUCT RECIPE HOOKS
// ============================================

export function useMaquilaRecipes(clientId?: string) {
  return useQuery({
    queryKey: [...MAQUILA_RECIPES_KEY, clientId],
    queryFn: () => maquilaService.getRecipes(clientId),
  });
}

export function useMaquilaRecipe(id: string | undefined) {
  return useQuery({
    queryKey: [...MAQUILA_RECIPES_KEY, id],
    queryFn: () => maquilaService.getRecipeById(id!),
    enabled: !!id,
  });
}

export function useCreateMaquilaRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: MaquilaRecipeFormData) =>
      maquilaService.createRecipe(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MAQUILA_RECIPES_KEY });
      toast.success("Receta creada exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al crear receta: ${error.message}`);
    },
  });
}

export function useUpdateMaquilaRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: MaquilaRecipeFormData }) =>
      maquilaService.updateRecipe(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MAQUILA_RECIPES_KEY });
      toast.success("Receta actualizada exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al actualizar receta: ${error.message}`);
    },
  });
}

export function useDeleteMaquilaRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => maquilaService.deleteRecipe(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MAQUILA_RECIPES_KEY });
      toast.success("Receta eliminada exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al eliminar receta: ${error.message}`);
    },
  });
}

// ============================================
// CONSUMPTION HOOKS
// ============================================

export function useMaquilaConsumptions(filters?: MaquilaConsumptionFilters) {
  return useQuery({
    queryKey: [...MAQUILA_CONSUMPTIONS_KEY, filters],
    queryFn: () => maquilaService.getConsumptions(filters),
  });
}

export function useMaquilaConsumption(id: string | undefined) {
  return useQuery({
    queryKey: [...MAQUILA_CONSUMPTIONS_KEY, id],
    queryFn: () => maquilaService.getConsumptionById(id!),
    enabled: !!id,
  });
}

export function useCreateMaquilaConsumption() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: MaquilaConsumptionInput) =>
      maquilaService.createConsumption(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MAQUILA_CONSUMPTIONS_KEY });
      // Consumption deducts Aliace ingredient stock + writes kardex movements.
      queryClient.invalidateQueries({ queryKey: ["oil_types"] });
      queryClient.invalidateQueries({ queryKey: ["raw_materials"] });
      queryClient.invalidateQueries({ queryKey: ["ingredient-movements"] });
      queryClient.invalidateQueries({ queryKey: ["kardex"] });
      toast.success("Consumo de maquila registrado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al registrar consumo: ${error.message}`);
    },
  });
}
